import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, Mail, Award, CheckCircle, MapPin, Star, ArrowRight, ShieldCheck, Clock, Users } from 'lucide-react';
import { fetchBySlug } from '../lib/cms';
import TestimonialSlider from '../components/TestimonialSlider';

const YDELSER = [
  {
    name: 'Tømrer',
    desc: 'Alt inden for tømrerfaget – fra mindre reparationer til store nybyggerier.',
    href: '/ydelser/toemrer',
    image: 'https://assets.auxodev.dk/assets/10033775-ad6c-49dd-a719-01817fe062a0/toemrer-galleri-1.jpg',
  },
  {
    name: 'Tag',
    desc: 'Lægning af alle former for tag samt professionel tagrenovering og efterisolering.',
    href: '/ydelser/tag',
    image: 'https://assets.auxodev.dk/assets/10033775-ad6c-49dd-a719-01817fe062a0/tag-galleri-1.jpg',
  },
  {
    name: 'Døre & Vinduer',
    desc: 'Udskiftning og montering af indvendige og udvendige døre og vinduer til fair priser.',
    href: '/ydelser/doere-vinduer',
    image: 'https://assets.auxodev.dk/assets/10033775-ad6c-49dd-a719-01817fe062a0/vinduer-galleri-1.jpg',
  },
  {
    name: 'Tilbygning',
    desc: 'Mangler du ekstra kvadratmeter? Vi bygger udestuer, badeværelser og ekstra rum.',
    href: '/ydelser/tilbygning',
    image: 'https://assets.auxodev.dk/assets/10033775-ad6c-49dd-a719-01817fe062a0/tilbygning.jpeg',
  },
  {
    name: 'Gulve',
    desc: 'Eksperter i lægning af alle typer gulve – klikgulve, massive trægulve og laminat.',
    href: '/ydelser/gulve',
    image: 'https://assets.auxodev.dk/assets/10033775-ad6c-49dd-a719-01817fe062a0/gulv-galleri-1.jpg',
  },
  {
    name: 'Montage',
    desc: 'Præcis og solid montering af køkkener, badeværelsesmøbler og garderober.',
    href: '/ydelser/montage',
    image: 'https://assets.auxodev.dk/assets/10033775-ad6c-49dd-a719-01817fe062a0/til-siden88-300x200.jpg',
  },
  {
    name: 'Hovedentrepriser',
    desc: 'Vi påtager os hele entreprisen og koordinerer alle faggrupper, så du kan slappe af.',
    href: '/ydelser/hovedentrepriser',
    image: 'https://assets.auxodev.dk/assets/10033775-ad6c-49dd-a719-01817fe062a0/hovedentreprise.jpg',
  },
];

export default function Home() {
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    async function loadPage() {
      const data = await fetchBySlug('forside');
      if (data) {
        setPageData(data);
      }
    }
    loadPage();
  }, []);

  const metaTitle = pageData?.meta_title?.da || "Tømrer i Malling ved Aarhus | KBS Tømrer-Montage ApS";
  const metaDesc = pageData?.meta_description?.da || "KBS Tømrer – Montage ApS i Malling udfører solidt tømrerarbejde, tag, døre, vinduer, tilbygninger, gulve, montage og hovedentrepriser med mere end 30 års erfaring.";

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <html lang="da" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-slate-950 text-white overflow-hidden py-24 lg:py-32">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/10033775-ad6c-49dd-a719-01817fe062a0/baggrund-22.jpg"
            alt="KBS Tømrer-Montage ApS"
            className="h-full w-full object-cover object-center opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/90 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 rounded-full bg-brand-primary/20 border border-brand-primary-light/30 px-4 py-1.5 text-xs sm:text-sm font-bold text-brand-primary-light mb-6 uppercase tracking-wider">
              <Award className="h-4 w-4" /> Mere end 30 års erfaring
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight">
              Godt og solidt håndværk <br />
              <span className="text-brand-primary-light">der holder i generationer</span>
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-slate-300 leading-relaxed max-w-2xl">
              Søger du en pålidelig tømrer? KBS Tømrer – Montage ApS leverer kompromisløs kvalitet, ærlighed og gennemsigtighed i Malling, Odder, Skanderborg, Ry og Århus.
            </p>

            {/* Key Selling Points */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
              <div className="flex items-center gap-2.5 text-sm font-semibold text-slate-200">
                <CheckCircle className="h-5 w-5 text-brand-primary-light shrink-0" />
                <span>30+ års erfaring i branchen</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm font-semibold text-slate-200">
                <CheckCircle className="h-5 w-5 text-brand-primary-light shrink-0" />
                <span>Medlem af 3byg (Topanbefalinger)</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm font-semibold text-slate-200">
                <CheckCircle className="h-5 w-5 text-brand-primary-light shrink-0" />
                <span>Lokalt forankret i Malling & Aarhus</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm font-semibold text-slate-200">
                <CheckCircle className="h-5 w-5 text-brand-primary-light shrink-0" />
                <span>Høj faglighed & overholdte aftaler</span>
              </div>
            </div>

            {/* Hero CTAs */}
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                className="px-8 py-4 text-base font-bold text-white bg-brand-primary hover:bg-brand-primary-dark rounded-xl shadow-lg hover:shadow-xl transition-all cursor-pointer"
              >
                Kontakt os nu
              </Link>
              <a
                href="#ydelser"
                className="px-8 py-4 text-base font-bold text-slate-200 bg-white/10 hover:bg-white/20 border border-white/10 rounded-xl transition-all cursor-pointer"
              >
                Se vores ydelser
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Boxes Section */}
      <section className="relative z-20 -mt-8 mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Phone Box */}
          <a
            href="tel:51219030"
            className="w-full min-w-0 flex items-center gap-6 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-xl hover:shadow-2xl hover:border-brand-primary/30 transition-all group"
          >
            <div className="flex-shrink-0 rounded-xl bg-brand-primary/10 p-4 text-brand-primary group-hover:bg-brand-primary group-hover:text-white transition-colors">
              <Phone className="h-8 w-8" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold text-brand-primary uppercase tracking-wider">Ring til os i dag</p>
              <p className="text-2xl sm:text-3xl font-display font-extrabold text-brand-charcoal mt-1 group-hover:text-brand-primary transition-colors truncate">
                51 21 90 30
              </p>
              <p className="text-sm text-slate-500 mt-1 truncate">Få en uforpligtende snak om dit projekt</p>
            </div>
          </a>

          {/* Email Box */}
          <a
            href="mailto:kbsoerensen@outlook.dk"
            className="w-full min-w-0 flex items-center gap-6 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-xl hover:shadow-2xl hover:border-brand-primary/30 transition-all group"
          >
            <div className="flex-shrink-0 rounded-xl bg-brand-primary/10 p-4 text-brand-primary group-hover:bg-brand-primary group-hover:text-white transition-colors">
              <Mail className="h-8 w-8" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold text-brand-primary uppercase tracking-wider">Skriv en e-mail</p>
              <p className="text-xl sm:text-2xl font-display font-extrabold text-brand-charcoal mt-1 group-hover:text-brand-primary transition-colors truncate">
                kbsoerensen@outlook.dk
              </p>
              <p className="text-sm text-slate-500 mt-1 truncate">Vi svarer hurtigt på alle henvendelser</p>
            </div>
          </a>
        </div>
      </section>

      {/* Intro Text from Scrape */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-brand-charcoal tracking-tight">
            Professionelt og ærligt tømrerfirma i Malling ved Århus
          </h2>
          <div className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed space-y-4 text-left border-l-4 border-brand-primary pl-6 italic">
            <p>
              Går du med tanker om et renoveringsprojekt, eller har du andre småprojekter derhjemme, du har brug for hjælp til at få fikset? Hos <strong>KBS Tømrer – Montage</strong> klarer vi alt inden for tømrerfaget, og vi står klar med løsninger til dit projekt.
            </p>
            <p>
              Vi har lokale samarbejdspartnere og hjælper gerne med en hel løsning til dit projekt.
            </p>
            <p>
              Vi holder til i <strong>Malling</strong>, men kører gerne til <strong>Århus</strong> og resten af <strong>Østjylland</strong> for private og erhvervskunder, der står med en interessant opgave.
            </p>
          </div>
          <div className="mt-10 flex justify-center">
            <Link
              to="/om-os"
              className="inline-flex items-center gap-2 text-sm font-bold text-brand-primary hover:text-brand-primary-dark transition-colors"
            >
              Læs mere om os og vores værdier <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section id="ydelser" className="py-20 bg-slate-50 border-y border-slate-100 w-full">
        <div className="w-full px-4 sm:px-8 lg:px-16 max-w-[1920px] mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-brand-charcoal tracking-tight">
              Hvad kan vi hjælpe dig med?
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Vi tilbyder en bred vifte af ydelser inden for tømrer- og montagearbejde. Klik på en ydelse for at læse mere og se galleri.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-6">
            {YDELSER.map((ydelse, index) => {
              // Optimer billeder via Cloudflare resizing (~600px bredde)
              const optimizedImg = ydelse.image.startsWith('https://assets.auxodev.dk')
                ? ydelse.image.replace('https://assets.auxodev.dk/assets/', 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/')
                : ydelse.image;

              // Første række (index 0-3): 4 kolonner på desktop (col-span-3)
              // Anden række (index 4-6): 3 kolonner på desktop (col-span-4) for at fylde hele bredden ud
              const gridSpanClass = index < 4 
                ? 'lg:col-span-3' 
                : 'lg:col-span-4';

              return (
                <Link
                  key={ydelse.name}
                  to={ydelse.href}
                  className={`group flex flex-col justify-between rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-xl hover:border-brand-primary/20 transition-all duration-300 ${gridSpanClass}`}
                >
                  <div>
                    <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                      <img
                        src={optimizedImg}
                        alt={ydelse.name}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                    <div className="p-6">
                      <h3 className="font-display text-xl font-bold text-brand-charcoal group-hover:text-brand-primary transition-colors">
                        {ydelse.name}
                      </h3>
                      <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                        {ydelse.desc}
                      </p>
                    </div>
                  </div>
                  <div className="px-6 pb-6 pt-2 flex items-center gap-1.5 text-sm font-bold text-brand-primary group-hover:text-brand-primary-dark transition-colors">
                    Læs mere <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Local Area Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-brand-primary/10 px-4 py-1.5 text-xs font-bold text-brand-primary mb-4 uppercase tracking-wider">
                <MapPin className="h-4 w-4" /> Lokal forankring
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-brand-charcoal tracking-tight">
                Din lokale tømrer i Malling og hele Østjylland
              </h2>
              <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
                Vi er stolte af vores stærke lokale rødder. Selvom vi har base i <span className="font-semibold text-brand-charcoal">Malling</span>, kører vi dagligt ud til spændende opgaver for både private og erhverv i hele regionen.
              </p>
              <p className="mt-3 text-base sm:text-lg text-slate-600 leading-relaxed">
                Vi dækker primært følgende byer og omegn:
              </p>

              {/* Area Badges */}
              <div className="mt-6 flex flex-wrap gap-3">
                {['Malling', 'Odder', 'Skanderborg', 'Ry', 'Århus'].map((by) => (
                  <span
                    key={by}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-50 border border-slate-200 text-sm font-bold text-brand-charcoal"
                  >
                    <span className="h-2 w-2 rounded-full bg-brand-primary" />
                    {by}
                  </span>
                ))}
              </div>

              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-3 text-sm text-slate-600">
                  <Clock className="h-5 w-5 text-brand-primary shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-brand-charcoal">Hurtig udrykning</p>
                    <p>Som lokalt firma kan vi hurtigt komme ud og besigtige din opgave.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 text-sm text-slate-600">
                  <Users className="h-5 w-5 text-brand-primary shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-brand-charcoal">Lokalt netværk</p>
                    <p>Vi samarbejder med de bedste lokale murere, elektrikere og VVS'ere.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-slate-200 shadow-xl">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/10033775-ad6c-49dd-a719-01817fe062a0/baggrund-1000.jpg"
                alt="KBS Tømrer-Montage lokalområde"
                className="h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 text-white">
                <p className="text-xs font-bold uppercase tracking-wider text-brand-primary-light">Balskærvej 26, Malling</p>
                <p className="text-lg font-bold font-display mt-0.5">Vores base siden stiftelsen</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Slider Section */}
      <section className="py-20 bg-slate-50 border-t border-slate-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-1 text-brand-gold mb-3">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-5 w-5 fill-current" />
              ))}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-brand-charcoal tracking-tight">
              Hvad siger vores kunder?
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Vi lever af tilfredse kunder. KBS Tømrer – Montage ApS er stolt medlem af 3byg, hvor vi har topkarakterer for vores tømrerarbejde.
            </p>
          </div>

          <TestimonialSlider />

          <div className="mt-10 text-center">
            <Link
              to="/anbefalinger"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-slate-200 bg-white text-sm font-bold text-brand-charcoal hover:bg-slate-50 transition-colors shadow-sm"
            >
              Se alle anbefalinger & 3byg profil
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="bg-brand-charcoal text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(#15803d_1px,transparent_1px)] [background-size:16px_16px]" />
        </div>
        <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            Skal vi hjælpe dig med dit næste projekt?
          </h2>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Uanset om det drejer sig om et nyt tag, udskiftning af vinduer, en tilbygning eller en mindre montageopgave, står vi klar med et uforpligtende tilbud.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/kontakt"
              className="px-8 py-4 text-base font-bold text-white bg-brand-primary hover:bg-brand-primary-dark rounded-xl shadow-lg transition-all cursor-pointer"
            >
              Få et uforpligtende tilbud
            </Link>
            <a
              href="tel:51219030"
              className="px-8 py-4 text-base font-bold text-slate-200 bg-white/10 hover:bg-white/20 border border-white/10 rounded-xl transition-all cursor-pointer"
            >
              Ring til Karsten: 51 21 90 30
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
